import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpResponse
} from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';
import { EncryptionService } from './encryption.service';

@Injectable()
export class EncryptionInterceptor implements HttpInterceptor {
  constructor(private encryptionService: EncryptionService) {}

  // Helper function to check if a string looks like encrypted data (iv:ciphertext format)
  private isEncryptedData(data: any): boolean {
    return typeof data === 'string' && 
           data.includes(':') && 
           data.split(':').length === 2 &&
           data.split(':')[0].length === 32 && // IV should be 32 hex chars (16 bytes)
           data.split(':')[1].length > 0;     // Ciphertext should exist
  }

  // Helper function to detect file uploads
  private isFileUpload(req: HttpRequest<any>): boolean {
    return req.body instanceof FormData || 
           req.url.includes('/fileUpload') || 
           req.url.includes('/upload') ||
           (req.headers.get('Content-Type')?.includes('multipart/form-data') ?? false);
  }

  // Helper function to handle response decryption
  private handleResponse(response: Observable<HttpEvent<any>>): Observable<HttpEvent<any>> {
    return response.pipe(
      switchMap(event => {
        if (event instanceof HttpResponse) {
          // console.log('Raw response received:', event.body);
          
          // Check if response body is directly encrypted string
          if (this.isEncryptedData(event.body)) {
            // console.log('Direct encrypted response found:', event.body);
            
            return from(this.encryptionService.decryptData(event.body)).pipe(
              map(decrypted => {
                // console.log('Decrypted response:', decrypted);
                return event.clone({ body: decrypted });
              })
            );
          }
          // Check if response has payload property
          else if (event.body?.payload && this.isEncryptedData(event.body.payload)) {
            // console.log('Payload encrypted response found:', event.body.payload);
            
            return from(this.encryptionService.decryptData(event.body.payload)).pipe(
              map(decrypted => {
                // console.log('Decrypted response:', decrypted);
                return event.clone({ body: decrypted });
              })
            );
          }
          // Check if any property in response object contains encrypted data
          else if (event.body && typeof event.body === 'object') {
            for (const key in event.body) {
              if (this.isEncryptedData(event.body[key])) {
                // console.log(`Encrypted data found in property '${key}':`, event.body[key]);
                
                return from(this.encryptionService.decryptData(event.body[key])).pipe(
                  map(decrypted => {
                    // console.log('Decrypted response:', decrypted);
                    return event.clone({ body: decrypted });
                  })
                );
              }
            }
          }
          
          // console.log('No encrypted data found, returning original response');
        }
        return from([event]);
      })
    );
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // console.log('Interceptor called for:', req.url);

    // Skip encryption for file uploads
    if (this.isFileUpload(req)) {
      // console.log('File upload detected, skipping encryption');
      return this.handleResponse(next.handle(req));
    }

    // If request has body → encrypt
    if (req.body) {
      return from(this.encryptionService.encryptData(req.body)).pipe(
        switchMap(encryptedBody => {
          const modifiedReq = req.clone({
            body: { payload: encryptedBody }   // 🔑 backend expects "payload"
          });
          
          // console.log('Encrypted request payload:', encryptedBody);

          return this.handleResponse(next.handle(modifiedReq));
        })
      );
    }

    // No body → still check response decryption
    return this.handleResponse(next.handle(req));
  }
}